/* reason: the prop-types flow seems not to work with classes */
/* eslint-disable react/prop-types */
import type { ElementType, ErrorInfo, PropsWithChildren, ReactNode } from 'react'

import { PureComponent } from 'react'
// reason: page component uses classes to be pased instead of type of page to be consumed (centered content etc etc), need to adjust that
// eslint-disable-next-line @dhl/generic/no-restricted-imports -- Use sx props or styled components to create CSS
import { makeStyles } from 'tss-react/mui'
import { InfoGraphic } from '@dhl/info-graphic'
import { CarParts } from '@dhl/illustrations'
import { Page } from '@dhl/page'

const useStyles = makeStyles()(() => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}))

type Props = {
  /**
   * When given we shor the user to go to a page with full url refresh
   */
  redirectUrl?: string

  /**
   * Customize your own title, when not given shows our default
   * @default 'components.errorBoundary.title'
   */
  title?: string

  /**
   * Customize your own explanation text, when not given shows our default
   * @default 'components.errorBoundary.text'
   */
  text?: string

  /**
   * When a redirectUrl is given, customize your own button cta text, when not given shows our default
   * @default 'components.errorBoundary.labelButton'
   */
  buttonLabel?: string

  /**
   * Hide the menu on the Page or your own `PageComponent`
   */
  hideMenu?: boolean

  /**
   * Put data-testid on the Page or your own `PageComponent`
   */
  testId?: string

  /**
   * When you do not want the dhl Page component to be wrapped arround the ErrorBoundary.
   * You can provide your own wrapper, for example SPA self scan has its own page layout, or receiver portal
   * make sure your Page component is not the cullprit of the original error :)
   */
  PageComponent?: ElementType
}

const ErrorBoundaryPage = ({
  testId,
  redirectUrl,
  title,
  text,
  buttonLabel,
  PageComponent,
  hideMenu = true,
}: Props) => {
  const { classes } = useStyles()

  const handleOnClick = () => {
    if (redirectUrl) {
      /**
       * Make sure we do a full page refresh
       */
      window.location.href = redirectUrl
    }
  }

  const Container = PageComponent || Page
  const mainTitle = title || 'components.errorBoundary.title'
  const mainText = text || 'components.errorBoundary.text'
  const ctaText = buttonLabel || 'components.errorBoundary.labelButton'

  return (
    <Container
      withoutRouter
      testId={testId}
      isReloadable={false}
      isLoading={false}
      hideMenu={hideMenu}
      classes={classes}
    >
      {redirectUrl ? (
        <InfoGraphic
          graphic={CarParts}
          title={mainTitle}
          description={mainText}
          cta={
            redirectUrl
              ? {
                  title: ctaText,
                  onClick: handleOnClick,
                }
              : undefined
          }
        />
      ) : (
        <InfoGraphic graphic={CarParts} title={mainTitle} description={mainText} />
      )}
    </Container>
  )
}

export type ErrorBoundaryDeprecatedProps = PropsWithChildren<
  Props & {
    /**
     * Integrate this callback `onError` with your own logging solution, so you have its data
     */
    onError?: (
      /**
       * The Error caught
       */
      error: Error,

      /**
       * The stacktrace belonging to the error
       */
      errorInfo: ErrorInfo
    ) => void
  }
>

type State = {
  /**
   * We got an uncaught Error
   */
  hasError: boolean

  /**
   * The caught Error itself
   */
  error?: Error
}

/**
 * @deprecated Built on the legacy `@dhl/page`, `@dhl/info-graphic` and `@dhl/illustrations` packages.
 * Use the design-system `ErrorBoundary` instead, which renders a `Blaze` and needs no page wrapper.
 */
export class ErrorBoundaryDeprecated extends PureComponent<ErrorBoundaryDeprecatedProps, State> {
  constructor(props: ErrorBoundaryDeprecatedProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { onError } = this.props

    onError?.(error, errorInfo)
  }

  render(): ReactNode {
    const { children, ...rest } = this.props
    const { hasError } = this.state

    if (hasError) {
      return <ErrorBoundaryPage testId="error-boundary" {...rest} />
    }
    return children
  }
}
